import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Lock, AlertCircle, Loader2, Award, Clock, BookOpen, FileQuestion, ArrowRight, CheckCircle2 } from "lucide-react"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import Card, { CardContent } from "./Card"
import { useAccessCheck } from "../hooks/useAccessCheck"
import { useAuth } from "../hooks/useAuth"
import { api } from "../utils/apiComponent/apiClient"
import { endpoints } from "../utils/endpoints"
import { showErrorToast, showSuccessToast } from "../utils/toast"
import { formatPrice } from "../utils/formatters"
import {
  saveEnrollmentIntent,
  clearEnrollmentIntent,
  getEnrollmentIntent,
  getLoginUrlWithContext,
  getSignupUrlWithContext
} from "../utils/enrollmentIntent"
import { isFreeCourse } from "../utils/courseUtils"
import { DELAYS } from "../utils/constants"
import { usePost } from "../utils/apiComponent/useApi"
import { QuizSkeleton } from "./skeletons"

/**
 * Composant de garde générique et centralisé pour protéger l'accès aux ressources
 * Supporte : courses, modules, lessons, blocks, quiz
 * 
 * Gère automatiquement :
 * - Vérification d'accès via /verify-access
 * - Authentification (modal élégant)
 * - Enrollment automatique pour les cours gratuits
 * - Paiement pour les cours payants
 * - Gestion de l'intention d'enrollment
 */
export default function AccessGuard({ 
  resourceType, // 'course' | 'module' | 'lesson' | 'block' | 'quiz'
  resourceId,
  course = null, // Données du cours (pour les modules/lessons/blocks/quiz)
  children,
  showLoading = true,
  redirectOnBlock = false,
  onAccessDenied = null,
  onEnrollmentSuccess = null,
  onPaymentRequest = null
}) {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { hasAccess, isChecking, reason, code, details, refetch } = useAccessCheck(resourceType, resourceId)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [isBarDismissed, setIsBarDismissed] = useState(false)
  const { execute: post } = usePost(endpoints.coursePayment)

  // Extraire courseId depuis resourceId ou course
  const courseId = resourceType === 'course' ? resourceId : (course?.id || details?.courseId)

  // Gérer l'enrollment automatique après connexion
  const handleAutoEnroll = useCallback(async () => {
    if (!isAuthenticated || !courseId || !course) return

    // Vérifier si c'est un cours gratuit ou payant
    if (!isFreeCourse(course)) {
      // Pour les cours payants, ouvrir le modal de paiement
      setIsEnrolling(false)
      if (onPaymentRequest) {
        onPaymentRequest()
      } else {
        // Fallback : redirection vers le paiement
        setIsEnrolling(true)
        try {
          const response = await post({ courseId: course.id })
          if (response?.checkoutLink) {
            window.open(response.checkoutLink, '_blank')
            showSuccessToast({ message: "Redirection vers le paiement..." })
          }
        } catch (err) {
          console.error("Erreur lors de la demande de paiement", err)
          showErrorToast({ message: "Erreur lors de la demande de paiement" })
        } finally {
          setIsEnrolling(false)
        }
      }
      return
    }

    // Cours gratuit : enrollment automatique
    setIsEnrolling(true)
    try {
      await api.get(endpoints.courseEnroll(courseId))
      
      // Rafraîchir la vérification d'accès
      await refetch()
      
      // Attendre un peu pour que l'état se mette à jour
      await new Promise(resolve => setTimeout(resolve, DELAYS.STATE_UPDATE))
      
      clearEnrollmentIntent()
      showSuccessToast({ 
        message: `Vous êtes maintenant inscrit à "${course.title}" !` 
      })
      
      if (onEnrollmentSuccess) {
        onEnrollmentSuccess()
      }
    } catch (err) {
      // Si erreur 400, peut être déjà enrôlé
      if (err?.status === 400) {
        await refetch()
        clearEnrollmentIntent()
        if (onEnrollmentSuccess) {
          onEnrollmentSuccess()
        }
      } else {
        showErrorToast({ 
          message: "Erreur lors de l'inscription. Veuillez réessayer." 
        })
      }
    } finally {
      setIsEnrolling(false)
    }
  }, [isAuthenticated, courseId, course, refetch, onEnrollmentSuccess, onPaymentRequest, post])

  // Auto-enrollment si l'utilisateur vient de se connecter
  useEffect(() => {
    if (isAuthenticated && !hasAccess && courseId && !isChecking && resourceType === 'course' && course) {
      const intent = getEnrollmentIntent()
      if (intent && intent.courseId === courseId) {
        if (isFreeCourse(course) || intent.isFree) {
          handleAutoEnroll()
        } else {
          clearEnrollmentIntent()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hasAccess, courseId, isChecking, resourceType])

  // Gérer le clic sur "S'inscrire au cours"
  const handleEnrollClick = useCallback(() => {
    if (!isAuthenticated) {
      // Sauvegarder l'intention d'enrollment
      if (course) {
        saveEnrollmentIntent({
          courseId,
          courseTitle: course.title,
          courseThumbnail: course.thumbnail,
          isFree: isFreeCourse(course),
          returnUrl: window.location.pathname
        })
      }
      
      // Afficher le modal d'authentification
      setShowAuthModal(true)
    } else {
      // L'utilisateur est connecté, essayer de s'enrôler
      handleAutoEnroll()
    }
  }, [isAuthenticated, course, courseId, handleAutoEnroll])

  // Redirection automatique selon le code d'erreur
  useEffect(() => {
    if (!isChecking && !hasAccess && redirectOnBlock && code) {
      if (code === 'USER_NOT_AUTHENTICATED') {
        navigate('/login', { state: { returnUrl: window.location.pathname } })
      } else if (code === 'NOT_ENROLLED' && courseId) {
        navigate(`/courses/${courseId}`)
      }
    }
  }, [hasAccess, isChecking, code, redirectOnBlock, navigate, courseId])

  // Callback personnalisé
  useEffect(() => {
    if (!isChecking && !hasAccess && onAccessDenied) {
      onAccessDenied({ reason, code, details })
    }
  }, [hasAccess, isChecking, reason, code, details, onAccessDenied])

  // État de chargement - Afficher le loader tant que la vérification est en cours
  // On attend que isChecking soit false ET qu'on ait reçu une réponse (hasAccess ou code)
  const hasReceivedResponse = hasAccess || code !== null
  
  if (isChecking || !hasReceivedResponse) {
    if (showLoading) {
      // Utiliser le skeleton approprié selon le type de ressource
      if (resourceType === 'quiz') {
        return <QuizSkeleton />
      }
      // Pour les autres ressources, afficher le loader simple
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
            <p className="text-gray-600">Vérification de l'accès...</p>
          </div>
        </div>
      )
    }
    // Si showLoading est false mais qu'on vérifie encore, ne rien afficher (ou afficher les children avec un état de chargement)
    return children
  }

  // Pour les pages de cours, permettre l'accès même si l'utilisateur n'est pas connecté
  // L'accès au contenu sera géré par les CTAs qui ouvriront le modal d'authentification
  if (resourceType === 'course') {
    // Permettre l'accès à la page du cours même sans authentification
    // Le modal d'authentification sera géré par les CTAs dans CourseDetailPage
    return children
  }

  // Accès autorisé OU certificat obtenu OU accès expiré
  if (hasAccess || code === 'CERTIFICATE_ISSUED' || code === 'ACCESS_EXPIRED') {
    return children
  }

  // Gérer la redirection vers login
  const handleLogin = () => {
    if (course) {
      saveEnrollmentIntent({
        courseId,
        courseTitle: course.title,
        courseThumbnail: course.thumbnail,
        isFree: isFreeCourse(course),
        returnUrl: window.location.pathname
      })
    }
    navigate(getLoginUrlWithContext(courseId))
    setShowAuthModal(false)
  }

  // Gérer la redirection vers signup
  const handleSignup = () => {
    if (course) {
      saveEnrollmentIntent({
        courseId,
        courseTitle: course.title,
        courseThumbnail: course.thumbnail,
        isFree: isFreeCourse(course),
        returnUrl: window.location.pathname
      })
    }
    navigate(getSignupUrlWithContext(courseId))
    setShowAuthModal(false)
  }

  // Si l'utilisateur n'est pas connecté et veut s'inscrire, afficher le modal
  if (!isAuthenticated && showAuthModal && resourceType === 'course' && course) {
    return (
      <>
        {/* Modal d'authentification */}
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAuthModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-10">
            {/* Header */}
            <div className="relative p-8 text-center border-b">
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setShowAuthModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Lock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Connectez-vous pour accéder à ce cours
              </h2>
              <p className="text-gray-600">
                Créez un compte ou connectez-vous pour commencer votre apprentissage
              </p>
            </div>

            {/* Course Preview */}
            {course && (
              <div className="p-6 border-b bg-gray-50">
                <div className="flex items-center gap-4">
                  {course.thumbnail && (
                    <img 
                      src={course.thumbnail} 
                      alt={course.title}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{course.title}</h3>
                    <div className="flex items-center gap-2">
                      {isFreeCourse(course) ? (
                        <Badge className="bg-green-100 text-green-800">Gratuit</Badge>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">
                          {formatPrice(course.price, course.currency)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="p-6">
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Accès limité à 6 mois dèss l'inscription au module</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Suivez votre progression</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Certificat de completion</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Accès limité à 6 mois dèss l'inscription au module</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleSignup}
                  className="w-full h-12 bg-[#141835] hover:bg-[#1a1f4a] text-white font-medium"
                >
                  Créer un compte
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                <Button
                  onClick={handleLogin}
                  variant="outline"
                  className="w-full h-12 font-medium"
                >
                  J'ai déjà un compte
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                En continuant, vous acceptez nos conditions d'utilisation
              </p>
            </div>
          </div>
        </div>

        {/* Children avec overlay */}
        <div className="opacity-30 pointer-events-none">
          {children}
        </div>
      </>
    )
  }

  // Si l'utilisateur est connecté mais pas encore enrôlé (pour les cours)
  if (isAuthenticated && !hasAccess && !isChecking && resourceType === 'course' && code === 'NOT_ENROLLED' && course && !isBarDismissed) {
    return (
      <>
        {children}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40 p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {course.thumbnail && (
                <img 
                  src={course.thumbnail} 
                  alt={course.title}
                  className="w-12 h-12 rounded object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{course.title}</h3>
                <p className="text-sm text-gray-600">
                  {isFreeCourse(course) 
                    ? "Cours gratuit" 
                    : formatPrice(course.price, course.currency)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => setIsBarDismissed(true)}
                variant="outline"
                size="sm"
                className="text-gray-600 hover:text-gray-900"
              >
                Plus tard
              </Button>
              <Button
                onClick={handleEnrollClick}
                disabled={isEnrolling}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                {isEnrolling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Inscription...
                  </>
                ) : (
                  <>
                    <BookOpen className="w-4 h-4 mr-2" />
                    S'inscrire au cours
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Accès refusé - Afficher le message approprié selon le code
  const renderErrorContent = () => {
    switch (code) {
      case 'USER_NOT_AUTHENTICATED':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Connexion requise
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Vous devez être connecté pour accéder à cette ressource"}</p>
            <Button
              onClick={() => navigate('/login', { state: { returnUrl: window.location.pathname } })}
              className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
            >
              Se connecter
            </Button>
          </div>
        )

      case 'MODULE_LESSONS_NOT_COMPLETED':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Chapitres non complétées
            </h2>
            <p className="text-gray-600 mb-4">{reason || "Vous devez compléter tous les chapitres du module avant de pouvoir passer le quiz"}</p>
            {details?.incompleteLessons && details.incompleteLessons.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm font-semibold text-orange-800 mb-2">
                  Chapitres restants ({details.incompleteLessons.length}) :
                </p>
                <ul className="space-y-1">
                  {details.incompleteLessons.map((lesson) => (
                    <li key={lesson.id} className="text-sm text-orange-700 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      {lesson.title}
                    </li>
                  ))}
                </ul>
                {details.moduleId && (
                  <Button
                    onClick={() => navigate(`/courses/${courseId}/modules/${details.moduleId}`)}
                    variant="outline"
                    className="mt-4 w-full border-orange-500 text-orange-600 hover:bg-orange-50"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Aller au module
                  </Button>
                )}
              </div>
            )}
            {details?.moduleId && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/modules/${details.moduleId}`)}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Compléter les Chapitres
              </Button>
            )}
          </div>
        )

      case 'QUIZ_ALREADY_PASSED':
        return (
          <div className="text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Quiz déjà validé
            </h2>
            <p className="text-gray-600 mb-4">{reason || "Vous avez déjà validé ce quiz"}</p>
            {details?.passedAttempt && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-1">
                  <strong>Score obtenu :</strong> {details.passedAttempt.scorePercentage}%
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Date :</strong> {new Date(details.passedAttempt.completedAt).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}
            {details?.moduleId && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/modules/${details.moduleId}`)}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au module
              </Button>
            )}
          </div>
        )

      case 'PREVIOUS_MODULE_NOT_COMPLETED':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Module précédent non complété
            </h2>
            <p className="text-gray-600 mb-4">{reason || "Vous devez valider le quiz du module précédent pour accéder à ce module"}</p>
            {details?.previousModuleId && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/modules/${details.previousModuleId}/quiz`)}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Aller au module précédent
              </Button>
            )}
          </div>
        )

      case 'COURSE_NOT_FOUND':
      case 'MODULE_NOT_FOUND':
      case 'LESSON_NOT_FOUND':
      case 'BLOCK_NOT_FOUND':
      case 'QUIZ_NOT_FOUND':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ressource non trouvée
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Cette ressource n'existe pas"}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/courses')}
            >
              Retour aux cours
            </Button>
          </div>
        )

      case 'COURSE_NOT_PUBLISHED':
      case 'COURSE_NOT_ACTIVE':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Cours non disponible
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Ce cours n'est pas encore disponible"}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/courses')}
            >
              Retour aux cours
            </Button>
          </div>
        )

      case 'NOT_ENROLLED':
        return (
          <div className="text-center">
            <BookOpen className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Inscription requise
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Vous devez être inscrit pour accéder à cette ressource"}</p>
            {courseId && (
              <Button
                onClick={handleEnrollClick}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                S'inscrire au cours
              </Button>
            )}
          </div>
        )

      case 'USER_NOT_ACTIVE':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Compte non actif
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Votre compte n'est pas actif"}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/profile')}
            >
              Voir mon profil
            </Button>
          </div>
        )

      case 'ENROLLMENT_DROPPED':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Inscription annulée
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Votre inscription a été annulée"}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/courses')}
            >
              Retour aux cours
            </Button>
          </div>
        )

      case 'CERTIFICATE_ISSUED':
        return (
          <div className="text-center">
            <Award className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Félicitations !
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Vous avez obtenu le certificat de ce cours"}</p>
            <Button
              onClick={() => navigate('/profile')}
              className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
            >
              Voir mon certificat
            </Button>
          </div>
        )

      case 'ACCESS_EXPIRED':
        return (
          <div className="text-center">
            <Clock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Accès expiré
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Votre période d'accès a expiré"}</p>
            <div className="space-y-3">
              <Button
                onClick={refetch}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                Vérifier à nouveau
              </Button>
              {courseId && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/courses/${courseId}`)}
                >
                  Voir les détails du cours
                </Button>
              )}
            </div>
          </div>
        )

      case 'ACCESS_MANUAL_BLOCK':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Accès bloqué
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Votre accès a été bloqué par un administrateur"}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/courses')}
            >
              Retour aux cours
            </Button>
          </div>
        )

      case 'PREVIOUS_LESSON_NOT_COMPLETED':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Chapitre précédent requis
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Vous devez compléter le chapitre précédente"}</p>
            {details?.previousLessonId && details?.moduleId && courseId && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/modules/${details.moduleId}/lessons/${details.previousLessonId}`)}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                Aller au chapitre précédent
              </Button>
            )}
            {details?.moduleId && courseId && !details?.previousLessonId && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/modules/${details.moduleId}`)}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                Retour au module
              </Button>
            )}
          </div>
        )

      case 'MODULE_BLOCKS_NOT_COMPLETED':
        return (
          <div className="text-center">
            <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Blocs non complétés
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Vous devez compléter tous les blocs du module"}</p>
            {details?.moduleId && courseId && (
              <Button
                onClick={() => navigate(`/courses/${courseId}/modules/${details.moduleId}`)}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                Retour au module
              </Button>
            )}
          </div>
        )

      default:
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Accès refusé
            </h2>
            <p className="text-gray-600 mb-6">{reason || "Vous n'avez pas accès à cette ressource"}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/courses')}
            >
              Retour aux cours
            </Button>
          </div>
        )
    }
  }


  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6">
          {renderErrorContent()}
        </CardContent>
      </Card>
    </div>
  )
}
